/**
 * PendingAgents — admin-only panel listing self-signup agents (email + Google)
 * awaiting approval. Approve → they can sign in; Reject → blocked. Both email
 * the agent via the backend (Resend).
 *
 * Surfaced on the Admin page (top) and the Team Management page.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserCheck, UserX, Mail, Phone, Loader2, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtWhen = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const PendingAgents = () => {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/users/pending`, { withCredentials: true })
      .then((res) => setPending(res.data || []))
      .catch(() => { /* non-admins simply see nothing */ })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (u, approve) => {
    setBusyId(u.user_id);
    try {
      await axios.post(`${BACKEND_URL}/api/users/${u.user_id}/${approve ? 'approve' : 'reject'}`, {}, { withCredentials: true });
      toast.success(`${u.name || u.email} ${approve ? 'approved' : 'rejected'}`);
      setPending((list) => list.filter((x) => x.user_id !== u.user_id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  if (user?.role !== 'admin') return null;

  return (
    <Card className="p-5 sm:p-6 mb-6 border-slate-200" data-testid="pending-agents">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="h-5 w-5 text-amber-600" />
        <h2 className="text-lg font-semibold text-slate-900">Pending Agents</h2>
        {pending.length > 0 && (
          <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-700" data-testid="pending-agents-count">
            {pending.length}
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 py-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
      ) : pending.length === 0 ? (
        <p className="text-sm text-slate-500 py-2" data-testid="pending-agents-empty">No agents awaiting approval.</p>
      ) : (
        <ul className="space-y-3">
          {pending.map((u) => (
            <li
              key={u.user_id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-slate-200 rounded-xl p-3"
              data-testid={`pending-agent-${u.user_id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold shrink-0">
                  {(u.name || u.email || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900 truncate">{u.name || '(no name)'}</p>
                    {u.auth_provider === 'google' && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-blue-100 text-blue-700">Google</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 flex-wrap">
                    <span className="flex items-center gap-1 min-w-0"><Mail className="h-3 w-3 shrink-0" /><span className="truncate">{u.email}</span></span>
                    {u.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{u.phone}</span>}
                    {u.created_at && <span className="text-slate-400">{fmtWhen(u.created_at)}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  onClick={() => act(u, true)}
                  disabled={busyId === u.user_id}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white min-h-[44px] sm:min-h-0"
                  data-testid={`approve-agent-${u.user_id}`}
                >
                  {busyId === u.user_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserCheck className="h-4 w-4 mr-1" /> Approve</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => act(u, false)}
                  disabled={busyId === u.user_id}
                  className="border-rose-200 text-rose-600 hover:bg-rose-50 min-h-[44px] sm:min-h-0"
                  data-testid={`reject-agent-${u.user_id}`}
                >
                  <UserX className="h-4 w-4 mr-1" /> Reject
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default PendingAgents;
